/**
 * PremiumExtraBlocks — Phase 7.1 + 7.2
 *
 * Public/preview rendering for the eight new block types added in Phase 7:
 *   7.1: testimonial · stats · contact-card · comparison
 *   7.2: countdown · pricing · timeline · faq
 *
 * Used by BOTH PublicDeckView.jsx (recipient view) and VisionDeckPreview.jsx
 * (agent preview) so the same markup ships to both audiences.
 *
 * Pure render — no state, no API calls, no side effects (except for the
 * countdown timer, which uses a small `useEffect` to tick every second, and
 * the FAQ accordion which uses local `useState` for expand/collapse).
 * Receives the same `(block, style)` envelope the existing PremiumBlock
 * switch uses.
 */
import { useState, useEffect } from 'react';
import {
  Quote,
  Mail,
  Phone,
  UserCircle,
  CheckCircle2,
  XCircle,
  Star,
  ChevronDown,
  Play,
  CalendarCheck,
} from 'lucide-react';
import { normalizeExternalUrl } from '../../lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export function renderPremiumExtraBlock(block, style) {
  if (!block || !style) return null;
  switch (block.type) {
    case 'countdown':
      return <CountdownBlock content={block.content || {}} style={style} />;
    case 'pricing':
      return <PricingBlock content={block.content || {}} style={style} />;
    case 'timeline':
      return <TimelineBlock content={block.content || {}} style={style} />;
    case 'faq':
      return <FaqBlock content={block.content || {}} style={style} />;
    case 'roi-calculator':
      return <RoiCalculatorBlock content={block.content || {}} style={style} />;
    case 'booking':
      return <BookingBlock content={block.content || {}} style={style} />;
    case 'lead-form':
      return <LeadFormBlock block={block} style={style} />;
    case 'video-testimonial':
      return <VideoTestimonialBlock content={block.content || {}} style={style} />;
    case 'testimonial': {
      const t = block.content || {};
      return (
        <div className="my-3 md:my-5 rounded-xl md:rounded-2xl p-6 md:p-10 border-l-4 max-w-4xl mx-auto" style={{ borderLeftColor: style.accent, backgroundColor: style.secondaryBg }}>
          <Quote className="w-8 h-8 md:w-10 md:h-10 mb-4 mx-auto" style={{ color: style.accent }} />
          <p className="italic font-serif text-lg md:text-2xl leading-relaxed text-center mb-5 md:mb-7" style={{ color: style.text }}>
            {t.quote}
          </p>
          <div className="h-px w-24 mx-auto mb-5" style={{ backgroundColor: style.accent }} />
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 mx-auto md:mx-0">
              {t.photo_url && (
                <img src={t.photo_url} alt={t.attribution_name || ''} className="w-10 h-10 rounded-full object-cover flex-shrink-0" style={{ outline: `2px solid ${style.accent}`, outlineOffset: 2 }} />
              )}
              <div className="text-sm md:text-base">
                {t.attribution_name && <span className="font-semibold" style={{ color: style.text }}>{t.attribution_name}</span>}
                {(t.attribution_title || t.attribution_company) && (
                  <span style={{ color: style.muted }}>
                    {t.attribution_name ? ' · ' : ''}{t.attribution_title}
                    {t.attribution_company ? ` · ${t.attribution_company}` : ''}
                  </span>
                )}
              </div>
            </div>
            {t.logo_url && (
              <img src={t.logo_url} alt={t.attribution_company || ''} className="h-8 md:h-10 max-h-10 w-auto object-contain opacity-80 mx-auto md:mx-0" />
            )}
          </div>
        </div>
      );
    }

    case 'stats': {
      const s = block.content || {};
      const stats = Array.isArray(s.stats) ? s.stats.filter((x) => x && (x.value || x.label)) : [];
      if (stats.length === 0 && !s.headline) return null;
      const cols = stats.length === 4 ? 'md:grid-cols-4' : stats.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';
      return (
        <div className="my-3 md:my-6 rounded-xl md:rounded-2xl p-8 md:p-12 text-center" style={{ backgroundColor: style.secondaryBg }}>
          {s.headline && (
            <h3 className="font-serif text-2xl md:text-3xl mb-6 md:mb-8 tracking-wide" style={{ color: style.text }}>
              {s.headline}
            </h3>
          )}
          <div className={`grid grid-cols-2 ${cols} gap-6 md:gap-2`}>
            {stats.map((st, idx) => (
              <div key={idx} className={`px-2 md:px-6 ${idx > 0 ? 'md:border-l' : ''}`} style={{ borderColor: `${style.accent}40` }}>
                <div className="font-serif font-bold leading-none mb-3" style={{ color: style.accent, fontSize: 'clamp(2rem, 6vw, 4.5rem)' }}>
                  {st.value || '—'}
                </div>
                <div className="text-xs md:text-sm uppercase tracking-widest" style={{ color: style.muted }}>
                  {st.label || ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    case 'contact-card': {
      const c = block.content || {};
      const phoneTel = (c.phone || '').replace(/[^0-9+]/g, '');
      return (
        <div className="my-3 md:my-5 rounded-xl md:rounded-2xl p-5 md:p-8 border max-w-3xl mx-auto flex flex-col md:flex-row items-center md:items-start gap-5 md:gap-8" style={{ backgroundColor: style.secondaryBg, borderColor: `${style.accent}40` }}>
          <div className="flex-shrink-0">
            {c.photo_url ? (
              <img src={c.photo_url} alt={c.name || ''} className="w-24 h-24 md:w-28 md:h-28 rounded-full object-cover" style={{ outline: `2px solid ${style.accent}`, outlineOffset: 3 }} />
            ) : (
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center" style={{ backgroundColor: `${style.accent}20`, outline: `2px solid ${style.accent}`, outlineOffset: 3 }}>
                <UserCircle className="w-14 h-14 md:w-16 md:h-16" style={{ color: style.accent }} />
              </div>
            )}
          </div>
          <div className="text-center md:text-left flex-1 min-w-0">
            {c.name && <div className="font-serif text-2xl md:text-3xl font-semibold mb-1" style={{ color: style.text }}>{c.name}</div>}
            {c.title && <div className="text-xs md:text-sm uppercase tracking-widest mb-3" style={{ color: style.accent }}>{c.title}</div>}
            {c.show_bio !== false && c.bio && (
              <p className="italic mb-4 leading-relaxed" style={{ color: style.muted }}>{c.bio}</p>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center justify-center md:justify-start gap-3 sm:gap-5 text-sm md:text-base">
              {c.show_email !== false && c.email && (
                <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 hover:underline" style={{ color: style.text }}>
                  <Mail className="w-4 h-4 md:w-5 md:h-5" style={{ color: style.accent }} />
                  {c.email}
                </a>
              )}
              {c.show_phone !== false && c.phone && (
                <a href={`tel:${phoneTel}`} className="inline-flex items-center gap-2 hover:underline" style={{ color: style.text }}>
                  <Phone className="w-4 h-4 md:w-5 md:h-5" style={{ color: style.accent }} />
                  {c.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      );
    }

    case 'comparison': {
      const cmp = block.content || {};
      const rows = Array.isArray(cmp.rows) ? cmp.rows.filter((r) => r && (r.left || r.right)) : [];
      if (rows.length === 0 && !cmp.headline) return null;
      const leftLabel = cmp.left_label || (cmp.mode === 'us-vs-them' ? 'Them' : 'Without Us');
      const rightLabel = cmp.right_label || (cmp.mode === 'us-vs-them' ? 'Us' : 'With Us');
      return (
        <div className="my-3 md:my-6 rounded-xl md:rounded-2xl overflow-hidden max-w-5xl mx-auto" style={{ backgroundColor: style.secondaryBg, border: `1px solid ${style.accent}30` }}>
          {cmp.headline && (
            <div className="px-5 md:px-8 py-5 md:py-6 text-center font-serif text-2xl md:text-3xl tracking-wide" style={{ color: style.text }}>
              {cmp.headline}
            </div>
          )}
          <div className="hidden md:grid grid-cols-2 text-sm font-semibold uppercase tracking-widest">
            <div className="px-6 md:px-8 py-4" style={{ color: style.muted, backgroundColor: 'rgba(0,0,0,0.03)' }}>{leftLabel}</div>
            <div className="px-6 md:px-8 py-4 border-l-4" style={{ color: style.accent, backgroundColor: `${style.accent}15`, borderLeftColor: style.accent }}>{rightLabel}</div>
          </div>
          <div className="divide-y" style={{ borderColor: `${style.accent}20` }}>
            {rows.map((r, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-2" style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.025)' }}>
                <div className="px-5 md:px-8 py-4 md:py-5 flex items-start gap-3 md:border-r" style={{ borderColor: `${style.accent}20` }}>
                  <span className="md:hidden text-[10px] font-semibold uppercase tracking-widest opacity-60 mr-1 mt-1 flex-shrink-0" style={{ color: style.muted }}>{leftLabel}:</span>
                  <XCircle className="w-4 h-4 md:w-5 md:h-5 mt-0.5 flex-shrink-0 opacity-50" style={{ color: style.muted }} />
                  <span className="text-sm md:text-base" style={{ color: style.muted }}>{r.left || <span className="opacity-40">—</span>}</span>
                </div>
                <div className="px-5 md:px-8 py-4 md:py-5 flex items-start gap-3 border-l-4" style={{ borderLeftColor: style.accent }}>
                  <span className="md:hidden text-[10px] font-semibold uppercase tracking-widest mr-1 mt-1 flex-shrink-0" style={{ color: style.accent }}>{rightLabel}:</span>
                  <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 mt-0.5 flex-shrink-0" style={{ color: style.accent }} />
                  <span className="text-sm md:text-base font-medium" style={{ color: style.text }}>{r.right || <span className="opacity-40">—</span>}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    default:
      return null;
  }
}


// ─────────────────────────────────────────────────────────────────────────────
// Phase 7.2 sub-components
// ─────────────────────────────────────────────────────────────────────────────

function diffParts(expiryIso) {
  if (!expiryIso) return null;
  const target = new Date(expiryIso).getTime();
  const now = Date.now();
  let diff = Math.max(0, target - now);
  const expired = diff === 0;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  diff -= days * 1000 * 60 * 60 * 24;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  diff -= hours * 1000 * 60 * 60;
  const minutes = Math.floor(diff / (1000 * 60));
  return { days, hours, minutes, expired };
}

function CountdownBlock({ content, style }) {
  const cd = content;
  const [parts, setParts] = useState(() => diffParts(cd.expiry_date));
  useEffect(() => {
    if (!cd.expiry_date) return;
    setParts(diffParts(cd.expiry_date));
    const t = setInterval(() => setParts(diffParts(cd.expiry_date)), 30_000);
    return () => clearInterval(t);
  }, [cd.expiry_date]);

  const expired = !cd.expiry_date || (parts && parts.expired);

  if (expired) {
    return (
      <div className="my-3 md:my-6 rounded-xl md:rounded-2xl p-8 md:p-12 text-center" style={{ backgroundColor: style.secondaryBg }}>
        {cd.headline && <div className="font-serif text-xl md:text-2xl mb-3" style={{ color: style.text }}>{cd.headline}</div>}
        <p className="italic" style={{ color: style.muted }}>{cd.expired_message || 'This offer has expired. Contact us to discuss next steps.'}</p>
      </div>
    );
  }

  const cells = [];
  if (cd.show_days !== false) cells.push({ label: 'Days', value: parts.days });
  if (cd.show_hours !== false) cells.push({ label: 'Hours', value: String(parts.hours).padStart(2, '0') });
  if (cd.show_minutes !== false) cells.push({ label: 'Minutes', value: String(parts.minutes).padStart(2, '0') });

  return (
    <div className="my-3 md:my-6 rounded-xl md:rounded-2xl p-8 md:p-12 text-center" style={{ backgroundColor: style.secondaryBg }}>
      <style>{`@keyframes deck-countdown-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.85; transform: scale(0.99); } }`}</style>
      {cd.headline && <div className="font-serif text-xl md:text-2xl mb-6" style={{ color: style.text }}>{cd.headline}</div>}
      <div className="flex items-baseline justify-center gap-3 md:gap-6">
        {cells.map((c, idx) => (
          <span key={c.label} className="flex items-baseline">
            {idx > 0 && <span className="font-serif text-3xl md:text-5xl mr-3 md:mr-6" style={{ color: style.accent }}>:</span>}
            <span className="text-center inline-block">
              <span className="font-serif font-bold leading-none block" style={{ color: style.accent, fontSize: 'clamp(2.25rem, 7vw, 5rem)', animation: 'deck-countdown-pulse 1.4s ease-in-out infinite' }}>
                {c.value}
              </span>
              <span className="text-[10px] md:text-xs uppercase tracking-widest mt-1 block" style={{ color: style.muted }}>{c.label}</span>
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function PricingBlock({ content, style }) {
  const p = content;
  const tiers = Array.isArray(p.tiers) ? p.tiers : [];
  if (tiers.length === 0 && !p.headline) return null;
  // Mobile: recommended tier first
  const sorted = [...tiers];
  const cols = sorted.length === 3 ? 'md:grid-cols-3' : sorted.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1';
  const needsPricing = !!p.needs_pricing;
  const placeholderText = p.pricing_placeholder_text || 'Add your pricing — click to edit';
  return (
    <div className="my-3 md:my-6 rounded-xl md:rounded-2xl p-6 md:p-10" style={{ backgroundColor: style.secondaryBg }}>
      {p.headline && (
        <h3 className="font-serif text-2xl md:text-3xl mb-6 md:mb-8 text-center" style={{ color: style.text }}>
          {p.headline}
        </h3>
      )}
      <div className={`grid grid-cols-1 ${cols} gap-5 md:gap-6 max-w-5xl mx-auto`}>
        {sorted.map((tier, idx) => {
          const recommended = !!tier.is_recommended;
          const orderClass = recommended ? 'order-first md:order-none' : '';
          const missingPrice = !tier.price || !String(tier.price).trim();
          const showPlaceholder = needsPricing && missingPrice;
          return (
            <div
              key={idx}
              className={`relative rounded-xl border p-6 flex flex-col ${recommended ? 'md:scale-105 md:my-2' : ''} ${orderClass}`}
              style={{
                borderColor: recommended ? style.accent : `${style.accent}30`,
                borderWidth: recommended ? 2 : 1,
                backgroundColor: recommended ? `${style.accent}10` : 'rgba(255,255,255,0.6)',
                boxShadow: recommended ? `0 8px 24px ${style.accent}25` : 'none',
              }}
            >
              {recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-white whitespace-nowrap" style={{ backgroundColor: style.accent }}>
                  <Star className="w-3 h-3 inline mr-1" /> Recommended
                </div>
              )}
              <div className="text-xs uppercase tracking-widest mb-3" style={{ color: style.accent }}>{tier.name || 'Tier'}</div>
              {showPlaceholder ? (
                <div
                  className="rounded-md border-2 border-dashed px-3 py-3 mb-3 text-center"
                  style={{ borderColor: '#D97706', backgroundColor: '#FFFBEB', color: '#B45309' }}
                  data-testid="pricing-needs-input"
                >
                  <div className="text-xs font-semibold uppercase tracking-widest mb-0.5">Pricing needed</div>
                  <div className="text-sm font-medium italic">{placeholderText}</div>
                </div>
              ) : (
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-serif font-bold leading-none" style={{ color: style.text, fontSize: 'clamp(2rem, 5vw, 3rem)' }}>
                    {p.currency_symbol || ''}{tier.price || '—'}
                  </span>
                </div>
              )}
              {tier.period && !showPlaceholder && <div className="text-xs mb-4" style={{ color: style.muted }}>{tier.period}</div>}
              {tier.description && <p className="italic text-sm mb-5" style={{ color: style.muted }}>{tier.description}</p>}
              <ul className="space-y-2 mb-6 flex-1">
                {(tier.features || []).map((f, fIdx) => (
                  <li key={fIdx} className="flex items-start gap-2 text-sm" style={{ color: style.text }}>
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: style.accent }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {(tier.cta_text || tier.cta_url) && (
                <a href={normalizeExternalUrl(tier.cta_url)} target={tier.cta_url ? '_blank' : undefined} rel="noopener noreferrer" className="block text-center px-4 py-3 rounded-md font-semibold text-sm transition" style={{ backgroundColor: recommended ? style.accent : 'transparent', color: recommended ? '#FFFFFF' : style.accent, border: `1.5px solid ${style.accent}` }}>
                  {tier.cta_text || 'Get Started'}
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TimelineBlock({ content, style }) {
  const tl = content;
  const items = Array.isArray(tl.items) ? tl.items : [];
  if (items.length === 0 && !tl.headline) return null;
  return (
    <div className="my-3 md:my-6 rounded-xl md:rounded-2xl p-6 md:p-10 max-w-4xl mx-auto" style={{ backgroundColor: style.secondaryBg }}>
      <style>{`@keyframes deck-fadein-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      {tl.headline && (
        <h3 className="font-serif text-2xl md:text-3xl mb-6 md:mb-8 text-center" style={{ color: style.text }}>
          {tl.headline}
        </h3>
      )}
      <div className="relative pl-10 md:pl-14">
        <div className="absolute left-3 md:left-5 top-2 bottom-2 w-0.5" style={{ backgroundColor: `${style.accent}50` }} />
        <div className="space-y-5 md:space-y-7">
          {items.map((it, idx) => (
            <div
              key={idx}
              className="relative"
              style={{ animation: `deck-fadein-up 600ms ease-out both`, animationDelay: `${Math.min(idx * 100, 600)}ms` }}
            >
              <div className="absolute -left-9 md:-left-12 top-1.5 w-4 h-4 md:w-5 md:h-5 rounded-full" style={{ backgroundColor: style.accent, outline: `4px solid ${style.secondaryBg}` }} />
              {it.period && <div className="text-[10px] md:text-xs uppercase tracking-widest mb-1" style={{ color: style.accent }}>{it.period}</div>}
              {it.title && <div className="font-serif text-lg md:text-2xl font-semibold mb-2" style={{ color: style.text }}>{it.title}</div>}
              {it.description && <p className="text-sm md:text-base leading-relaxed" style={{ color: style.muted }}>{it.description}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FaqBlock({ content, style }) {
  const f = content;
  const items = Array.isArray(f.items) ? f.items.filter((it) => it && (it.question || it.answer)) : [];
  const [openIdx, setOpenIdx] = useState(-1);
  if (items.length === 0 && !f.headline) return null;
  return (
    <div className="my-3 md:my-6 rounded-xl md:rounded-2xl p-6 md:p-10 max-w-4xl mx-auto" style={{ backgroundColor: style.secondaryBg }}>
      {f.headline && (
        <h3 className="font-serif text-2xl md:text-3xl mb-6 md:mb-8 text-center" style={{ color: style.text }}>
          {f.headline}
        </h3>
      )}
      <div className="divide-y" style={{ borderColor: `${style.accent}30` }}>
        {items.map((it, idx) => {
          const open = openIdx === idx;
          return (
            <div key={idx} className="py-1">
              <button
                type="button"
                onClick={() => setOpenIdx(open ? -1 : idx)}
                className="w-full flex items-center justify-between gap-4 py-4 md:py-5 text-left transition-colors"
                aria-expanded={open}
              >
                <span className="font-semibold text-base md:text-lg" style={{ color: style.text }}>{it.question}</span>
                <ChevronDown
                  className="w-5 h-5 flex-shrink-0 transition-transform duration-300"
                  style={{ color: style.accent, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </button>
              <div
                className="overflow-hidden transition-all duration-300 ease-out"
                style={{ maxHeight: open ? '600px' : '0px', opacity: open ? 1 : 0 }}
              >
                <p className="pb-5 pr-10 text-sm md:text-base leading-relaxed" style={{ color: style.muted }}>
                  {it.answer}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 7.3 sub-components
// ─────────────────────────────────────────────────────────────────────────────

function evalFormula(formula, vars) {
  if (!formula || typeof formula !== 'string') return null;
  const allowedKeys = Object.keys(vars || {});
  const stripped = formula.replace(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g, (m) =>
    allowedKeys.includes(m) ? '0' : `__BAD__${m}__`
  );
  if (stripped.includes('__BAD__')) return null;
  if (!/^[\s\d+\-*/().,]*$/.test(stripped)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(...allowedKeys, `return (${formula});`);
    const out = fn(...allowedKeys.map((k) => Number(vars[k]) || 0));
    if (typeof out !== 'number' || !isFinite(out)) return null;
    return out;
  } catch {
    return null;
  }
}

function formatRoiNumber(n, prefix, suffix) {
  if (n === null || n === undefined || !isFinite(n)) return '—';
  let formatted;
  if (Math.abs(n) >= 1000) formatted = Math.round(n).toLocaleString();
  else if (Math.abs(n) >= 10) formatted = n.toFixed(0);
  else formatted = n.toFixed(2);
  return `${prefix || ''}${formatted}${suffix || ''}`;
}

function RoiCalculatorBlock({ content, style }) {
  const r = content;
  const inputs = Array.isArray(r.inputs) ? r.inputs : [];
  const outputs = Array.isArray(r.outputs) ? r.outputs : [];
  const [vars, setVars] = useState(() =>
    Object.fromEntries(inputs.map((i) => [i.id, Number(i.default_value) || 0]))
  );
  const setVar = (id, value) => setVars((prev) => ({ ...prev, [id]: Number(value) }));

  return (
    <div className="my-3 md:my-6 rounded-xl md:rounded-2xl p-6 md:p-10 max-w-4xl mx-auto" style={{ backgroundColor: style.secondaryBg }}>
      {r.headline && <h3 className="font-serif text-2xl md:text-3xl text-center mb-2" style={{ color: style.text }}>{r.headline}</h3>}
      {r.subheadline && <p className="italic text-center mb-7 md:mb-9" style={{ color: style.muted }}>{r.subheadline}</p>}
      <div className="space-y-6 md:space-y-7 mb-8">
        {inputs.map((inp) => {
          const min = Number(inp.min) || 0;
          const max = Number(inp.max) || 100;
          const v = Number(vars[inp.id] ?? inp.default_value ?? min);
          const pct = max === min ? 0 : ((v - min) / (max - min)) * 100;
          return (
            <div key={inp.id}>
              <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                <label className="text-sm md:text-base font-medium" style={{ color: style.text }}>{inp.label}</label>
                <span className="font-serif font-bold text-xl md:text-2xl" style={{ color: style.accent }}>
                  {(inp.prefix || '') + Math.round(v).toLocaleString() + (inp.suffix || '')}
                </span>
              </div>
              <input
                type="range"
                min={min}
                max={max}
                step={(max - min) >= 1000 ? Math.max(1, Math.round((max - min) / 100)) : 1}
                value={v}
                onChange={(e) => setVar(inp.id, e.target.value)}
                className="w-full h-2 rounded-full appearance-none cursor-pointer roi-slider"
                style={{ background: `linear-gradient(to right, ${style.accent} 0%, ${style.accent} ${pct}%, ${style.accent}30 ${pct}%, ${style.accent}30 100%)` }}
              />
              <div className="flex justify-between text-[10px] md:text-xs mt-1" style={{ color: style.muted }}>
                <span>{(inp.prefix || '') + min.toLocaleString() + (inp.suffix || '')}</span>
                <span>{(inp.prefix || '') + max.toLocaleString() + (inp.suffix || '')}</span>
              </div>
            </div>
          );
        })}
      </div>
      <style>{`
        .roi-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 22px; height: 22px; border-radius: 9999px; background: ${style.accent}; box-shadow: 0 0 0 4px ${style.accent}30; cursor: pointer; transition: transform 0.15s; }
        .roi-slider::-webkit-slider-thumb:hover { transform: scale(1.12); }
        .roi-slider::-moz-range-thumb { width: 22px; height: 22px; border: 0; border-radius: 9999px; background: ${style.accent}; box-shadow: 0 0 0 4px ${style.accent}30; cursor: pointer; }
      `}</style>
      <div className="h-px mb-7" style={{ backgroundColor: `${style.accent}40` }} />
      <div className="space-y-3 md:space-y-4">
        {outputs.map((out) => {
          const value = evalFormula(out.formula, vars);
          const display = formatRoiNumber(value, out.prefix, out.suffix);
          if (out.highlight) {
            return (
              <div key={out.id} className="rounded-xl p-5 md:p-6 text-center" style={{ backgroundColor: `${style.accent}15`, border: `1.5px solid ${style.accent}` }}>
                <div className="text-xs md:text-sm uppercase tracking-widest mb-2" style={{ color: style.muted }}>{out.label}</div>
                <div className="font-serif font-bold leading-none" style={{ color: style.accent, fontSize: 'clamp(2.25rem, 6vw, 4rem)' }}>{display}</div>
              </div>
            );
          }
          return (
            <div key={out.id} className="flex items-center justify-between gap-4 px-2">
              <div className="text-xs md:text-sm uppercase tracking-widest" style={{ color: style.muted }}>{out.label}</div>
              <div className="font-serif text-xl md:text-3xl font-bold" style={{ color: style.text }}>{display}</div>
            </div>
          );
        })}
      </div>
      {(r.cta_text || r.cta_url) && (
        <div className="mt-8 text-center">
          <a href={normalizeExternalUrl(r.cta_url)} target={r.cta_url ? '_blank' : undefined} rel="noopener noreferrer" className="inline-block px-8 py-3 rounded-md font-semibold text-sm md:text-base transition" style={{ backgroundColor: style.accent, color: '#FFFFFF' }}>
            {r.cta_text || 'Get Started'}
          </a>
        </div>
      )}
    </div>
  );
}

function isCalendlyOrCalDotCom(url) {
  if (!url || typeof url !== 'string') return false;
  return /(?:calendly\.com|cal\.com)\//i.test(url);
}

function BookingBlock({ content, style }) {
  const b = content;
  const showInline = !!b.show_inline && isCalendlyOrCalDotCom(b.booking_url);
  return (
    <div className="my-3 md:my-6 rounded-xl md:rounded-2xl p-6 md:p-10 max-w-4xl mx-auto text-center" style={{ backgroundColor: style.secondaryBg }}>
      {b.headline && <h3 className="font-serif text-2xl md:text-3xl mb-2" style={{ color: style.text }}>{b.headline}</h3>}
      {b.subheadline && <p className="italic mb-6 md:mb-8 max-w-2xl mx-auto" style={{ color: style.muted }}>{b.subheadline}</p>}
      {showInline ? (
        <div className="rounded-xl overflow-hidden bg-white" style={{ border: `1px solid ${style.accent}30`, height: '650px' }}>
          <iframe
            src={b.booking_url}
            title="Schedule a call"
            className="w-full h-full border-0"
            loading="lazy"
            allow="camera; microphone; autoplay; encrypted-media; fullscreen; picture-in-picture"
          />
        </div>
      ) : b.booking_url ? (
        <a
          href={normalizeExternalUrl(b.booking_url)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-md font-semibold text-base md:text-lg transition hover:opacity-90"
          style={{ backgroundColor: style.accent, color: '#FFFFFF' }}
        >
          <CalendarCheck className="w-5 h-5" />
          {b.button_text || 'Schedule Your Call'}
        </a>
      ) : (
        <p className="italic" style={{ color: style.muted }}>(Add a booking URL in the editor to enable this block.)</p>
      )}
    </div>
  );
}

function LeadFormBlock({ block, style }) {
  const lf = block.content || {};
  const fields = Array.isArray(lf.fields) ? lf.fields : [];
  const [values, setValues] = useState(() => Object.fromEntries(fields.map((f) => [f.id, ''])));
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const getDeckId = () => {
    const m1 = window.location.pathname.match(/\/vision-decks\/([0-9a-f-]+)/);
    if (m1) return m1[1];
    const m2 = window.location.pathname.match(/\/d\/([\w-]+)/);
    return m2 ? m2[1] : null;
  };

  const onChange = (id, val) => setValues((prev) => ({ ...prev, [id]: val }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    for (const f of fields) {
      if (f.required && !(values[f.id] || '').trim()) {
        setErrorMsg(`Please fill in “${f.label}”.`);
        return;
      }
    }
    const deckId = getDeckId();
    if (!deckId) {
      setSuccess(true);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/vision-decks/${deckId}/form-submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          block_id: block.id,
          notify_email: lf.notify_email || null,
          fields: values,
        }),
      });
      if (!res.ok) throw new Error('failed');
      setSuccess(true);
    } catch {
      setErrorMsg('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="my-3 md:my-6 rounded-xl md:rounded-2xl p-8 md:p-12 max-w-2xl mx-auto text-center" style={{ backgroundColor: style.secondaryBg, border: `1px solid ${style.accent}30` }} data-testid="lead-form-success">
        <style>{`@keyframes deck-success-pop { 0% { transform: scale(0.6); opacity: 0; } 60% { transform: scale(1.08); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }`}</style>
        <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${style.accent}25`, animation: 'deck-success-pop 600ms ease-out both' }}>
          <CheckCircle2 className="w-10 h-10 md:w-12 md:h-12" style={{ color: style.accent }} />
        </div>
        <p className="font-serif text-xl md:text-2xl" style={{ color: style.text }}>
          {lf.success_message || 'Thank you! We will be in touch within one business day.'}
        </p>
      </div>
    );
  }

  return (
    <div className="my-3 md:my-6 rounded-xl md:rounded-2xl p-6 md:p-10 max-w-2xl mx-auto" style={{ backgroundColor: style.secondaryBg }}>
      {lf.headline && <h3 className="font-serif text-2xl md:text-3xl text-center mb-2" style={{ color: style.text }}>{lf.headline}</h3>}
      {lf.subheadline && <p className="italic text-center mb-7 md:mb-8" style={{ color: style.muted }}>{lf.subheadline}</p>}
      <form onSubmit={onSubmit} className="space-y-4" data-testid="lead-form">
        {fields.map((f) => (
          <div key={f.id}>
            <label className="block text-sm font-medium mb-1.5" style={{ color: style.text }}>
              {f.label}
              {f.required && <span style={{ color: style.accent }}> *</span>}
            </label>
            {f.type === 'textarea' ? (
              <textarea
                value={values[f.id] || ''}
                onChange={(e) => onChange(f.id, e.target.value)}
                required={!!f.required}
                rows={4}
                className="w-full px-4 py-3 rounded-md outline-none bg-white"
                style={{ border: `1.5px solid ${style.accent}40`, color: style.text }}
                onFocus={(e) => (e.target.style.borderColor = style.accent)}
                onBlur={(e) => (e.target.style.borderColor = `${style.accent}40`)}
              />
            ) : (
              <input
                type={f.type || 'text'}
                value={values[f.id] || ''}
                onChange={(e) => onChange(f.id, e.target.value)}
                required={!!f.required}
                className="w-full px-4 py-3 rounded-md outline-none bg-white"
                style={{ border: `1.5px solid ${style.accent}40`, color: style.text }}
                onFocus={(e) => (e.target.style.borderColor = style.accent)}
                onBlur={(e) => (e.target.style.borderColor = `${style.accent}40`)}
              />
            )}
          </div>
        ))}
        {errorMsg && (
          <div className="text-sm rounded-md px-3 py-2" style={{ color: '#B91C1C', backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }} data-testid="lead-form-error">
            {errorMsg}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="w-full px-6 py-3 rounded-md font-semibold text-base transition disabled:opacity-60"
          style={{ backgroundColor: style.accent, color: '#FFFFFF' }}
          data-testid="lead-form-submit"
        >
          {submitting ? 'Sending…' : (lf.submit_text || 'Send My Request')}
        </button>
      </form>
    </div>
  );
}

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{11})/);
  return m ? m[1] : null;
}
function getVimeoId(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

function VideoTestimonialBlock({ content, style }) {
  const vt = content;
  const ytId = getYouTubeId(vt.video_url);
  const vimeoId = getVimeoId(vt.video_url);
  const autoThumb = ytId ? `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg` : '';
  const thumbnail = vt.thumbnail_url || autoThumb;
  const [playing, setPlaying] = useState(false);

  const renderPlayer = () => {
    if (ytId) {
      return (
        <iframe
          src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
          title="Video testimonial"
          className="w-full h-full border-0"
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
          allowFullScreen
        />
      );
    }
    if (vimeoId) {
      return (
        <iframe
          src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1`}
          title="Video testimonial"
          className="w-full h-full border-0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      );
    }
    if (vt.video_url) {
      return <video src={vt.video_url} controls autoPlay className="w-full h-full bg-black" />;
    }
    return null;
  };

  return (
    <div className="my-3 md:my-6 max-w-4xl mx-auto px-2 md:px-0">
      {vt.headline && <h3 className="font-serif text-2xl md:text-3xl text-center mb-5" style={{ color: style.text }}>{vt.headline}</h3>}
      <div className="rounded-xl md:rounded-2xl overflow-hidden bg-black relative" style={{ aspectRatio: '16/9', boxShadow: `0 16px 40px rgba(0,0,0,0.18)` }}>
        {playing ? (
          renderPlayer()
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="absolute inset-0 w-full h-full group"
            aria-label="Play video"
          >
            {thumbnail ? (
              <img src={thumbnail} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: style.secondaryBg }}>
                <Play className="w-12 h-12" style={{ color: style.muted }} />
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
              <span className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ backgroundColor: style.accent, boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
                <Play className="w-8 h-8 md:w-10 md:h-10 ml-1 text-white" fill="white" />
              </span>
            </div>
          </button>
        )}
      </div>
      {(vt.attribution_name || vt.attribution_title || vt.attribution_company || vt.caption) && (
        <div className="mt-4 md:mt-5 text-center md:text-left">
          <div className="text-sm md:text-base">
            {vt.attribution_name && <span className="font-semibold" style={{ color: style.text }}>{vt.attribution_name}</span>}
            {(vt.attribution_title || vt.attribution_company) && (
              <span style={{ color: style.muted }}>
                {vt.attribution_name ? ' · ' : ''}{vt.attribution_title}
                {vt.attribution_company ? ` · ${vt.attribution_company}` : ''}
              </span>
            )}
          </div>
          {vt.caption && <p className="italic mt-1 text-sm md:text-base" style={{ color: style.muted }}>{vt.caption}</p>}
        </div>
      )}
    </div>
  );
}

